import { useState, useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { usePricing, type PricingConfig } from "@/data/PricingContext";
import { DollarSign, Ruler, Layers, Wrench, RotateCcw, Save, Plus, X, AlertTriangle, History, Clock, Percent } from "lucide-react";
import type { BathroomSystemType, BathroomMaterial } from "@/data/types";
import type { DimensionPreset } from "@/data/pricingEngine";
import { toast } from "sonner";

const SYSTEM_LABELS: Record<BathroomSystemType, string> = {
  "Walk-in Shower": "Walk-in Shower",
  "Standard Tub": "Standard Tub",
  "Walk-in Tub": "Walk-in Tub",
  "Jetted Tub": "Jetted Tub",
};

const MATERIAL_LABELS: Record<BathroomMaterial, string> = {
  Sentrel: "Sentrel (PVC Composite)",
  Dekton: "Dekton (Porcelain Composite)",
  Tile: "Tile",
};

interface AuditEntry {
  id: string;
  timestamp: string;
  user: string;
  field: string;
  oldValue: string;
  newValue: string;
}

export function PricingAdmin() {
  const { config, setConfig, resetToDefaults } = usePricing();
  const [draft, setDraft] = useState<PricingConfig>(JSON.parse(JSON.stringify(config)));
  const [showAddPreset, setShowAddPreset] = useState(false);
  const [newPreset, setNewPreset] = useState({ width: "", length: "", adjustment: "" });

  // Global multiplier
  const [globalMultiplier, setGlobalMultiplier] = useState(1.0);
  const [showMultiplierApply, setShowMultiplierApply] = useState(false);

  // Dual pricing toggle
  const [showRetail, setShowRetail] = useState(false);
  const retailMarkup = 1.15; // 15% retail markup

  // Warning prompts
  const [pendingSave, setPendingSave] = useState(false);
  const [warningMessage, setWarningMessage] = useState("");

  // Audit dismissal
  const [auditDismissed, setAuditDismissed] = useState(false);

  // Audit history
  const [auditLog] = useState<AuditEntry[]>([
    { id: "pa1", timestamp: "2026-03-26 14:20", user: "Mike Reynolds", field: "Walk-in Shower Base", oldValue: "$8,500", newValue: "$9,200" },
    { id: "pa2", timestamp: "2026-03-25 10:15", user: "Sarah Chen", field: "Dekton Multiplier", oldValue: "1.45×", newValue: "1.50×" },
    { id: "pa3", timestamp: "2026-03-24 16:30", user: "Mike Reynolds", field: "Plumbing Relocation", oldValue: "$1,000", newValue: "$1,200" },
    { id: "pa4", timestamp: "2026-03-22 09:00", user: "James Porter", field: "60\" × 36\" Adjustment", oldValue: "$800", newValue: "$950" },
    { id: "pa5", timestamp: "2026-03-20 11:45", user: "Sarah Chen", field: "Custom Modifications", oldValue: "$600", newValue: "$800" },
  ]);

  const updateSystemPrice = (key: BathroomSystemType, value: string) => {
    const num = parseFloat(value) || 0;
    setDraft((d) => ({ ...d, systemBasePrices: { ...d.systemBasePrices, [key]: num } }));
  };

  const updateMaterial = (key: BathroomMaterial, value: string) => {
    const num = parseFloat(value) || 1;
    setDraft((d) => ({ ...d, materialMultipliers: { ...d.materialMultipliers, [key]: num } }));
  };

  const updateDimensionAdj = (idx: number, value: string) => {
    const num = parseFloat(value) || 0;
    setDraft((d) => {
      const presets = [...d.dimensionPresets];
      presets[idx] = { ...presets[idx], adjustment: num };
      return { ...d, dimensionPresets: presets };
    });
  };

  const removePreset = (idx: number) => {
    setDraft((d) => ({ ...d, dimensionPresets: d.dimensionPresets.filter((_, i) => i !== idx) }));
  };

  const addPreset = () => {
    const w = parseInt(newPreset.width) || 0;
    const l = parseInt(newPreset.length) || 0;
    const a = parseFloat(newPreset.adjustment) || 0;
    if (w <= 0 || l <= 0) return;
    const preset: DimensionPreset = { label: `${w}" × ${l}"`, width: w, length: l, adjustment: a };
    setDraft((d) => ({ ...d, dimensionPresets: [...d.dimensionPresets, preset] }));
    setNewPreset({ width: "", length: "", adjustment: "" });
    setShowAddPreset(false);
  };

  const updateComplexity = (key: keyof PricingConfig["complexityAdders"], value: string) => {
    const num = parseFloat(value) || 0;
    setDraft((d) => ({ ...d, complexityAdders: { ...d.complexityAdders, [key]: num } }));
  };

  // Check for large changes before saving
  const checkAndSave = () => {
    const warnings: string[] = [];
    // Check base price changes > 20%
    for (const key of Object.keys(SYSTEM_LABELS) as BathroomSystemType[]) {
      const old = config.systemBasePrices[key];
      const cur = draft.systemBasePrices[key];
      if (old > 0) {
        const pctChange = Math.abs((cur - old) / old) * 100;
        if (pctChange > 20) {
          warnings.push(`${SYSTEM_LABELS[key]} changed by ${pctChange.toFixed(0)}%`);
        }
      }
    }
    // Check multiplier changes > 15%
    for (const key of Object.keys(MATERIAL_LABELS) as BathroomMaterial[]) {
      const old = config.materialMultipliers[key];
      const cur = draft.materialMultipliers[key];
      if (Math.abs(cur - old) > 0.15) {
        warnings.push(`${key} multiplier changed by ${((cur - old) * 100).toFixed(0)}%`);
      }
    }

    if (warnings.length > 0) {
      setWarningMessage(warnings.join(". ") + ". This may significantly impact active quotes.");
      setPendingSave(true);
    } else {
      doSave();
    }
  };

  const doSave = () => {
    setConfig(draft);
    setPendingSave(false);
    toast.success("Pricing configuration saved");
  };

  const handleReset = () => {
    resetToDefaults();
    setDraft(JSON.parse(JSON.stringify(config)));
    toast.info("Pricing reset to defaults");
  };

  const applyGlobalMultiplier = () => {
    if (globalMultiplier === 1.0) return;
    setDraft(d => ({
      ...d,
      systemBasePrices: Object.fromEntries(
        Object.entries(d.systemBasePrices).map(([k, v]) => [k, Math.round((v as number) * globalMultiplier)])
      ) as Record<BathroomSystemType, number>,
    }));
    setShowMultiplierApply(false);
    setGlobalMultiplier(1.0);
    toast.success(`Global multiplier of ${globalMultiplier}× applied to all base prices`);
  };

  const hasChanges = JSON.stringify(draft) !== JSON.stringify(config);

  return (
    <div className="space-y-4">
      {/* Header bar */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h2 className="text-sm font-semibold flex items-center gap-2">
            <DollarSign className="h-4 w-4 text-primary" /> Pricing Configuration
          </h2>
          <p className="text-xs text-muted-foreground mt-0.5">
            Adjust pricing inputs that power the quote engine. Changes apply immediately.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {/* Dual pricing toggle */}
          <div className="flex items-center gap-1.5 border rounded-md px-2 py-1">
            <span className="text-[10px] text-muted-foreground">Show Retail</span>
            <Switch checked={showRetail} onCheckedChange={setShowRetail} />
          </div>
          {hasChanges && <Badge variant="outline" className="text-[10px] bg-warning/10 text-warning border-warning/40">Unsaved</Badge>}
          <Button size="sm" variant="outline" className="text-xs h-7 gap-1" onClick={handleReset}>
            <RotateCcw className="h-3 w-3" /> Reset
          </Button>
          <Button size="sm" className="text-xs h-7 gap-1" onClick={checkAndSave} disabled={!hasChanges}>
            <Save className="h-3 w-3" /> Save
          </Button>
        </div>
      </div>

      {/* Global Multiplier */}
      <Card className="border-dashed">
        <CardContent className="p-3 flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <div className="h-8 w-8 rounded-lg bg-primary/10 flex items-center justify-center">
              <Percent className="h-4 w-4 text-primary" />
            </div>
            <div>
              <p className="text-xs font-semibold">Global Multiplier</p>
              <p className="text-[10px] text-muted-foreground">Apply a percentage adjustment across all base prices at once</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Input
              type="number"
              step="0.05"
              value={globalMultiplier}
              onChange={(e) => setGlobalMultiplier(parseFloat(e.target.value) || 1)}
              className="w-20 text-xs h-8 text-center"
            />
            <span className="text-xs text-muted-foreground">×</span>
            <Button size="sm" variant="outline" className="text-xs h-7" disabled={globalMultiplier === 1.0} onClick={() => setShowMultiplierApply(true)}>
              Apply
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* ── Base System Pricing ── */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-xs flex items-center gap-1.5"><DollarSign className="h-3.5 w-3.5 text-primary" /> Base System Prices</CardTitle>
          <CardDescription className="text-[11px]">Starting price for each system type before adjustments</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {(Object.keys(SYSTEM_LABELS) as BathroomSystemType[]).map((key) => (
              <div key={key} className="space-y-1">
                <Label className="text-xs">{SYSTEM_LABELS[key]}</Label>
                <div className="relative">
                  <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">$</span>
                  <Input
                    type="number"
                    value={draft.systemBasePrices[key]}
                    onChange={(e) => updateSystemPrice(key, e.target.value)}
                    className="pl-6 text-xs h-8"
                  />
                </div>
                {showRetail && (
                  <p className="text-[10px] text-muted-foreground">
                    Retail: ${Math.round(draft.systemBasePrices[key] * retailMarkup).toLocaleString()}
                  </p>
                )}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* ── Dimension Adjustments ── */}
      <Card>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-xs flex items-center gap-1.5"><Ruler className="h-3.5 w-3.5 text-primary" /> Dimension Adjustments</CardTitle>
              <CardDescription className="text-[11px]">Price adjustments for preset base dimensions (W × L)</CardDescription>
            </div>
            <Button size="sm" variant="outline" className="text-xs h-7 gap-1" onClick={() => setShowAddPreset(true)}>
              <Plus className="h-3 w-3" /> Add Preset
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-2">
          <div className="grid gap-2">
            {draft.dimensionPresets.map((preset, idx) => (
              <div key={idx} className="flex items-center gap-2 p-2 rounded-md bg-muted/40 border">
                <span className="text-xs font-medium min-w-[80px]">{preset.label}</span>
                <span className="text-[10px] text-muted-foreground">({preset.width}×{preset.length})</span>
                <div className="flex-1" />
                <Label className="text-[10px] text-muted-foreground">Adj:</Label>
                <div className="relative w-24">
                  <span className="absolute left-2 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">$</span>
                  <Input type="number" value={preset.adjustment} onChange={(e) => updateDimensionAdj(idx, e.target.value)} className="pl-5 text-xs h-7 w-full" />
                </div>
                {showRetail && <span className="text-[10px] text-muted-foreground">R: ${Math.round(preset.adjustment * retailMarkup)}</span>}
                <Button size="sm" variant="ghost" className="h-6 w-6 p-0" onClick={() => removePreset(idx)}>
                  <X className="h-3 w-3 text-muted-foreground" />
                </Button>
              </div>
            ))}
          </div>

          {showAddPreset && (
            <div className="flex items-end gap-2 p-2 rounded-md border border-dashed bg-muted/20">
              <div className="space-y-1">
                <Label className="text-[10px]">Width"</Label>
                <Input type="number" value={newPreset.width} onChange={(e) => setNewPreset((p) => ({ ...p, width: e.target.value }))} className="text-xs h-7 w-16" />
              </div>
              <div className="space-y-1">
                <Label className="text-[10px]">Length"</Label>
                <Input type="number" value={newPreset.length} onChange={(e) => setNewPreset((p) => ({ ...p, length: e.target.value }))} className="text-xs h-7 w-16" />
              </div>
              <div className="space-y-1">
                <Label className="text-[10px]">Adjustment $</Label>
                <Input type="number" value={newPreset.adjustment} onChange={(e) => setNewPreset((p) => ({ ...p, adjustment: e.target.value }))} className="text-xs h-7 w-20" />
              </div>
              <Button size="sm" className="text-xs h-7" onClick={addPreset}>Add</Button>
              <Button size="sm" variant="ghost" className="text-xs h-7" onClick={() => setShowAddPreset(false)}>Cancel</Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* ── Material Multipliers ── */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-xs flex items-center gap-1.5"><Layers className="h-3.5 w-3.5 text-primary" /> Material Multipliers</CardTitle>
          <CardDescription className="text-[11px]">Multiplier applied to base system price (1.0 = no change)</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {(Object.keys(MATERIAL_LABELS) as BathroomMaterial[]).map((key) => (
              <div key={key} className="space-y-1">
                <Label className="text-xs">{MATERIAL_LABELS[key]}</Label>
                <div className="relative">
                  <Input
                    type="number" step="0.05" value={draft.materialMultipliers[key]}
                    onChange={(e) => updateMaterial(key, e.target.value)} className="text-xs h-8 pr-8"
                  />
                  <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">×</span>
                </div>
                {key !== "Sentrel" && (
                  <p className="text-[10px] text-muted-foreground">+{Math.round((draft.materialMultipliers[key] - 1) * 100)}% on base</p>
                )}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* ── Complexity Adders ── */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-xs flex items-center gap-1.5"><Wrench className="h-3.5 w-3.5 text-primary" /> Complexity Adders</CardTitle>
          <CardDescription className="text-[11px]">Flat dollar amounts added based on job complexity factors</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div>
            <Label className="text-[11px] font-medium text-muted-foreground mb-1.5 block">Labor Complexity</Label>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {(["standard", "moderate", "high"] as const).map((level) => (
                <div key={level} className="space-y-1">
                  <Label className="text-xs capitalize">{level}</Label>
                  <div className="relative">
                    <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">$</span>
                    <Input type="number" value={draft.complexityAdders[level]} onChange={(e) => updateComplexity(level, e.target.value)} className="pl-6 text-xs h-8" />
                  </div>
                  {showRetail && <p className="text-[10px] text-muted-foreground">R: ${Math.round(draft.complexityAdders[level] * retailMarkup)}</p>}
                </div>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-xs">Plumbing Relocation</Label>
              <div className="relative">
                <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">$</span>
                <Input type="number" value={draft.complexityAdders.plumbingRelocation} onChange={(e) => updateComplexity("plumbingRelocation", e.target.value)} className="pl-6 text-xs h-8" />
              </div>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Custom Modifications</Label>
              <div className="relative">
                <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">$</span>
                <Input type="number" value={draft.complexityAdders.customModifications} onChange={(e) => updateComplexity("customModifications", e.target.value)} className="pl-6 text-xs h-8" />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* ── Audit History ── */}
      {!auditDismissed && (
        <Card>
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-xs flex items-center gap-1.5"><History className="h-3.5 w-3.5 text-primary" /> Pricing Change History</CardTitle>
              <Button size="sm" variant="ghost" className="text-[10px] h-6" onClick={() => setAuditDismissed(true)}>
                <X className="h-3 w-3 mr-1" /> Dismiss
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-0 divide-y">
              {auditLog.map((entry) => (
                <div key={entry.id} className="flex items-center gap-3 py-2 first:pt-0 last:pb-0 text-xs">
                  <Clock className="h-3 w-3 text-muted-foreground shrink-0" />
                  <span className="text-muted-foreground whitespace-nowrap">{entry.timestamp}</span>
                  <span className="font-medium">{entry.user}</span>
                  <span className="text-muted-foreground">changed</span>
                  <span className="font-medium">{entry.field}</span>
                  <span className="text-destructive/70 line-through">{entry.oldValue}</span>
                  <span className="text-success font-medium">{entry.newValue}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* ═══ DIALOGS ═══ */}

      {/* Warning prompt for large changes */}
      <Dialog open={pendingSave} onOpenChange={(open) => !open && setPendingSave(false)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-sm flex items-center gap-2 text-warning">
              <AlertTriangle className="h-4 w-4" /> Significant Price Change
            </DialogTitle>
            <DialogDescription className="text-xs">{warningMessage}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setPendingSave(false)}>Cancel</Button>
            <Button size="sm" onClick={doSave}>Confirm & Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Global multiplier confirmation */}
      <Dialog open={showMultiplierApply} onOpenChange={setShowMultiplierApply}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-sm">Apply Global Multiplier</DialogTitle>
            <DialogDescription className="text-xs">
              This will multiply all base system prices by {globalMultiplier}×. This action modifies draft values and must be saved separately.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setShowMultiplierApply(false)}>Cancel</Button>
            <Button size="sm" onClick={applyGlobalMultiplier}>Apply {globalMultiplier}×</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
